import { useState, useEffect, useRef } from 'react'
import { ProductsService } from '../services/api/productsService'
import { Product } from '../types/product'
import { logger } from '../utils/logger'

interface UseProductDetailsReturn {
  product: Product | null
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export const useProductDetails = (id?: string): UseProductDetailsReturn => {
  const [product, setProduct] = useState<Product | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const lastIdRef = useRef<string | undefined>(undefined)
  const fetchPromiseRef = useRef<Promise<Product> | null>(null)

  const fetchProduct = async (productId: string): Promise<Product> => {
    // If we're already fetching this product, return the existing promise
    if (fetchPromiseRef.current && lastIdRef.current === productId) {
      return fetchPromiseRef.current
    }

    setIsLoading(true)
    setError(null)
    lastIdRef.current = productId

    // Create new fetch promise
    fetchPromiseRef.current = ProductsService.getProduct(productId)

    try {
      const data = await fetchPromiseRef.current
      setProduct(data)
      return data
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch product'
      logger.error('Error fetching product:', err)
      setError(errorMessage)
      setProduct(null)
      throw err
    } finally {
      setIsLoading(false)
      fetchPromiseRef.current = null
    }
  }

  const refetch = async () => {
    if (id) {
      // Clear cache and fetch again
      fetchPromiseRef.current = null
      lastIdRef.current = undefined
      await fetchProduct(id)
    }
  }

  useEffect(() => {
    if (id && id !== lastIdRef.current) {
      fetchProduct(id)
    } else if (!id) {
      // Reset when no ID
      setProduct(null)
      setError(null)
      setIsLoading(false)
      lastIdRef.current = undefined
      fetchPromiseRef.current = null
    }
  }, [id])

  return {
    product,
    isLoading,
    error,
    refetch
  }
}